import { ApiService } from './api-services/ApiService.js';
import { logger } from '../utils/logger.js';
import { ApiResponse } from '../types/api.js';

/**
 * Service de données unifié pour les opérations CRUD communes
 * Réduit la duplication entre services spécialisés
 */
export class UnifiedDataService {
  private static instance: UnifiedDataService;
  private apiService = ApiService.getInstance();

  private constructor() {}

  static getInstance(): UnifiedDataService {
    if (!UnifiedDataService.instance) {
      UnifiedDataService.instance = new UnifiedDataService();
    }
    return UnifiedDataService.instance;
  }

  // Opérations CRUD génériques
  async create<T>(endpoint: string, data: Partial<T>): Promise<T> {
    try {
      const response = await this.apiService.post<ApiResponse<T>>(endpoint, data);
      if (response.data) return response.data;
      throw new Error(response.message || 'Erreur lors de la création');
    } catch (error) {
      logger.error(`Erreur création ${endpoint}:`, error);
      throw error;
    }
  }

  async read<T>(endpoint: string, id?: string | number): Promise<T> {
    try {
      const url = id ? `${endpoint}/${id}` : endpoint;
      const response = await this.apiService.get<ApiResponse<T>>(url);
      if (response.data) return response.data;
      throw new Error(response.message || 'Erreur lors de la lecture');
    } catch (error) {
      logger.error(`Erreur lecture ${endpoint}:`, error);
      throw error;
    }
  }

  async update<T>(endpoint: string, id: string | number, data: Partial<T>): Promise<T> {
    try {
      const response = await this.apiService.put<ApiResponse<T>>(`${endpoint}/${id}`, data);
      if (response.data) return response.data;
      throw new Error(response.message || 'Erreur lors de la mise à jour');
    } catch (error) {
      logger.error(`Erreur mise à jour ${endpoint}/${id}:`, error);
      throw error;
    }
  }

  async delete(endpoint: string, id: string | number): Promise<void> {
    try {
      await this.apiService.delete(`${endpoint}/${id}`);
    } catch (error) {
      logger.error(`Erreur suppression ${endpoint}/${id}:`, error);
      throw error;
    }
  }

  async list<T>(endpoint: string, params?: Record<string, unknown>): Promise<T[]> {
    try {
      const response = await this.apiService.get<ApiResponse<T[]>>(endpoint, { params });
      return response.data || [];
    } catch (error) {
      logger.error(`Erreur liste ${endpoint}:`, error);
      throw error;
    }
  }

  // Opérations spécialisées communes
  async search<T>(endpoint: string, query: string, filters?: Record<string, unknown>): Promise<T[]> {
    try {
      const params = { q: query, ...filters };
      const response = await this.apiService.get<ApiResponse<T[]>>(`${endpoint}/search`, { params });
      return response.data || [];
    } catch (error) {
      logger.error(`Erreur recherche ${endpoint}:`, error);
      throw error;
    }
  }

  async getByStatus<T>(endpoint: string, status: string): Promise<T[]> {
    try {
      const response = await this.apiService.get<ApiResponse<T[]>>(`${endpoint}/status/${status}`);
      return response.data || [];
    } catch (error) {
      logger.error(`Erreur récupération par statut ${endpoint}/${status}:`, error);
      throw error;
    }
  }

  async bulkUpdate<T>(endpoint: string, updates: Array<{ id: string | number; data: Partial<T> }>): Promise<T[]> {
    try {
      const response = await this.apiService.put<ApiResponse<T[]>>(`${endpoint}/bulk`, { updates });
      return response.data || [];
    } catch (error) {
      logger.error(`Erreur mise à jour groupée ${endpoint}:`, error);
      throw error;
    }
  }
}

// Instance globale
export const dataService = UnifiedDataService.getInstance();
